import * as React from 'react';
import { ArrowPathIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { useUiStore } from '@/store/uiStore';
import { creditFeedbackService } from '@/services/creditFeedbackService';
import type {
  BillingCreditPackage,
  CreditUsageRate,
  CurrentSubscription,
  SubscriptionPlanOption,
} from '@/types/billing';
import { useBilling } from '../hooks/useBilling';
import {
  formatBillingDate,
  formatBillingInterval,
  formatMinorUnits,
  formatUsage,
} from '../utils/formatBilling';

interface BillingViewProps {
  compact?: boolean;
}

type BillingAvailability = 'both' | 'membership' | 'credits' | 'none';

const billingAvailabilityCopy: Record<
  BillingAvailability,
  {
    heading: string;
    sectionTitle: string;
    sectionDescription: string;
  }
> = {
  both: {
    heading: 'Choose how you want to pay',
    sectionTitle: 'Payment options',
    sectionDescription: 'Review both models before choosing checkout.',
  },
  membership: {
    heading: 'Choose your membership',
    sectionTitle: 'Membership options',
    sectionDescription: 'Review the available memberships before choosing checkout.',
  },
  credits: {
    heading: 'Buy credits when you need them',
    sectionTitle: 'Pay as you go',
    sectionDescription: 'Choose a one-time credit package.',
  },
  none: {
    heading: 'Payment options',
    sectionTitle: 'Payment options',
    sectionDescription: 'New purchases are temporarily unavailable.',
  },
};

function resolveBillingAvailability(
  hasMembershipOptions: boolean,
  payAsYouGoEnabled: boolean
): BillingAvailability {
  if (hasMembershipOptions && payAsYouGoEnabled) return 'both';
  if (hasMembershipOptions) return 'membership';
  if (payAsYouGoEnabled) return 'credits';
  return 'none';
}

function StatusNotice({ subscription }: { subscription: CurrentSubscription }) {
  const hasBillingIssue =
    subscription.status === 'past_due' ||
    subscription.status === 'unpaid' ||
    subscription.status === 'incomplete' ||
    subscription.access_state === 'grace_period';

  if (hasBillingIssue) {
    return (
      <div
        className='flex gap-3 rounded-12 border border-yellow-400 bg-yellow-400/10 p-4 text-brand-800'
        role='status'
      >
        <ExclamationTriangleIcon className='mt-0.5 size-5 shrink-0' aria-hidden='true' />
        <div>
          <p className='font-sans text-14 font-bold'>Payment needs attention</p>
          <p className='mt-1 font-sans text-13 leading-20'>
            Update your payment method to avoid losing membership access.
          </p>
        </div>
      </div>
    );
  }

  if (subscription.cancel_at_period_end) {
    return (
      <div className='rounded-12 border border-purple-220 bg-purple-25 p-4' role='status'>
        <p className='font-sans text-14 font-bold text-brand-800'>Renewal is canceled</p>
        <p className='mt-1 font-sans text-13 leading-20 text-gray-650'>
          Your purchased benefits remain available through{' '}
          {formatBillingDate(subscription.current_period_end)}.
        </p>
      </div>
    );
  }

  return null;
}

function CurrentMembershipCard({
  subscription,
  onManage,
  onCancel,
  isManaging,
}: {
  subscription: CurrentSubscription;
  onManage: () => void;
  onCancel: () => void;
  isManaging: boolean;
}) {
  const plan = subscription.purchased_plan;
  const dateLabel = subscription.cancel_at_period_end ? 'Access through' : 'Renews on';

  return (
    <section aria-labelledby='current-membership-title' className='flex flex-col gap-5'>
      <div className='flex flex-col gap-1'>
        <p className='font-sans text-13 font-bold uppercase tracking-wide text-purple-680'>
          Current membership
        </p>
        <div className='flex flex-col justify-between gap-2 sm:flex-row sm:items-end'>
          <div>
            <h2
              id='current-membership-title'
              className='font-sans text-24 font-bold text-brand-800'
            >
              {plan.name}
            </h2>
            <p className='mt-1 font-sans text-14 text-gray-650'>{plan.summary}</p>
          </div>
          <p className='font-sans text-18 font-bold text-brand-800'>
            {formatMinorUnits(plan.price_amount_minor, plan.currency)}{' '}
            <span className='text-13 font-normal text-gray-530'>{formatBillingInterval(plan)}</span>
          </p>
        </div>
      </div>

      <StatusNotice subscription={subscription} />

      <div className='grid gap-px overflow-hidden rounded-12 border border-purple-150 bg-purple-150 sm:grid-cols-3'>
        <div className='bg-white p-4'>
          <p className='font-sans text-12 font-medium uppercase tracking-wide text-gray-530'>
            {dateLabel}
          </p>
          <p className='mt-1 font-sans text-16 font-bold text-brand-800'>
            {formatBillingDate(subscription.current_period_end)}
          </p>
        </div>
        <div className='bg-white p-4'>
          <p className='font-sans text-12 font-medium uppercase tracking-wide text-gray-530'>
            Conversations this period
          </p>
          <p className='mt-1 font-sans text-16 font-bold text-brand-800'>
            {subscription.usage_summary.event_count.toLocaleString()}
          </p>
        </div>
        <div className='bg-white p-4'>
          <p className='font-sans text-12 font-medium uppercase tracking-wide text-gray-530'>
            Weighted usage
          </p>
          <p className='mt-1 font-sans text-16 font-bold text-brand-800'>
            {formatUsage(subscription.usage_summary.weighted_units)} units
          </p>
        </div>
      </div>

      <div className='grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end'>
        <div>
          <h3 className='font-sans text-14 font-bold text-brand-800'>Purchased benefits</h3>
          <ul className='mt-2 grid gap-2 sm:grid-cols-2'>
            {plan.benefits.map((benefit) => {
              const usage = subscription.usage_summary.capabilities.find(
                (item) => item.code === benefit.code
              );
              return (
                <li key={benefit.code} className='flex gap-2 font-sans text-13 text-gray-650'>
                  <CheckIcon className='mt-0.5 size-4 shrink-0 text-green-700' aria-hidden='true' />
                  <span>
                    <strong className='font-semibold text-brand-800'>{benefit.name}</strong>
                    {usage ? ` · ${usage.event_count.toLocaleString()} used` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <div className='flex flex-col items-start gap-2 lg:items-end'>
          <div className='flex flex-wrap gap-3'>
            {subscription.management_mode === 'stripe_portal' && (
              <Button
                type='button'
                variant='create-echo'
                className='min-h-11 px-5 text-14 font-bold text-brand-800'
                onClick={onManage}
                disabled={isManaging}
              >
                {isManaging ? 'Opening billing…' : 'Manage billing'}
              </Button>
            )}
            {subscription.can_cancel_renewal && (
              <button
                type='button'
                onClick={onCancel}
                className='min-h-11 rounded-30 px-4 font-sans text-14 font-semibold text-red-600 underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-800'
              >
                Cancel renewal
              </button>
            )}
          </div>
          {subscription.management_mode === 'external_store' && (
            <p className='max-w-72 font-sans text-13 leading-20 text-gray-650 lg:text-right'>
              Manage or cancel this membership in the app store where you purchased it.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function MembershipOption({
  plan,
  isSelected,
  hasCurrentSubscription,
  isCheckingOut,
  onSelect,
  onCheckout,
}: {
  plan: SubscriptionPlanOption;
  isSelected: boolean;
  hasCurrentSubscription: boolean;
  isCheckingOut: boolean;
  onSelect: () => void;
  onCheckout: () => void;
}) {
  return (
    <article
      className={clsx(
        'flex h-full flex-col rounded-16 border bg-white p-5',
        isSelected ? 'border-brand-800 ring-1 ring-brand-800' : 'border-purple-150'
      )}
    >
      <button
        type='button'
        className='text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-800'
        onClick={onSelect}
        aria-pressed={isSelected}
      >
        <span className='font-sans text-18 font-bold text-brand-800'>{plan.name}</span>
        <span className='mt-1 block font-sans text-13 leading-20 text-gray-650'>
          {plan.summary}
        </span>
      </button>
      <p className='mt-4 font-sans text-24 font-bold text-brand-800'>
        {formatMinorUnits(plan.price_amount_minor, plan.currency)}
        <span className='ml-1 text-13 font-normal text-gray-530'>
          {formatBillingInterval(plan)}
        </span>
      </p>
      <ul className='my-4 flex flex-1 flex-col gap-2'>
        {plan.benefits.map((benefit) => (
          <li key={benefit.code} className='flex gap-2 font-sans text-13 text-gray-650'>
            <CheckIcon className='mt-0.5 size-4 shrink-0 text-green-700' aria-hidden='true' />
            <span>
              <strong className='font-semibold text-brand-800'>{benefit.name}</strong>
              <span className='block text-12 leading-18'>{benefit.description}</span>
            </span>
          </li>
        ))}
      </ul>
      <Button
        type='button'
        variant='purple-dark'
        className='min-h-11 w-full text-14 font-bold text-white'
        onClick={onCheckout}
        disabled={hasCurrentSubscription || isCheckingOut}
      >
        {hasCurrentSubscription
          ? 'Membership already active'
          : isCheckingOut
            ? 'Opening checkout…'
            : `Choose ${plan.name}`}
      </Button>
    </article>
  );
}

function CreditPackageOption({
  creditPackage,
  isPurchasing,
  disabled,
  onPurchase,
}: {
  creditPackage: BillingCreditPackage;
  isPurchasing: boolean;
  disabled: boolean;
  onPurchase: () => void;
}) {
  return (
    <article className='flex min-w-60 flex-1 flex-col rounded-16 border border-purple-150 bg-white p-4'>
      {creditPackage.is_featured && (
        <p className='mb-2 font-sans text-11 font-bold uppercase tracking-wide text-purple-680'>
          Most popular
        </p>
      )}
      <h3 className='font-sans text-16 font-bold text-brand-800'>{creditPackage.name}</h3>
      <p className='mt-1 flex-1 font-sans text-12 leading-18 text-gray-530'>
        {creditPackage.description ?? 'Credits for conversations with any Echo.'}
      </p>
      <p className='mt-4 font-sans text-20 font-bold text-brand-800'>
        {creditPackage.credits.toLocaleString()}{' '}
        <span className='text-13 font-normal text-gray-530'>credits</span>
      </p>
      <p className='mt-1 font-sans text-16 font-semibold text-brand-800'>
        {formatMinorUnits(creditPackage.price_amount_minor, creditPackage.currency)}
      </p>
      <Button
        type='button'
        variant='create-echo'
        className='mt-4 min-h-11 w-full text-14 font-bold text-brand-800'
        onClick={onPurchase}
        disabled={disabled}
      >
        {isPurchasing ? 'Opening checkout…' : `Buy ${creditPackage.credits} credits`}
      </Button>
    </article>
  );
}

function membershipCapability(
  plan: SubscriptionPlanOption | undefined,
  rate: CreditUsageRate
): string {
  const benefit = plan?.benefits.find((item) => item.code === rate.capability && item.included);
  return benefit ? benefit.name : 'Uses credits';
}

function ComparisonTable({
  plan,
  rates,
}: {
  plan: SubscriptionPlanOption | undefined;
  rates: CreditUsageRate[];
}) {
  const policyRows = [
    {
      label: 'Renewal',
      membership: plan
        ? `Renews every ${plan.interval_count === 1 ? plan.interval_unit : `${plan.interval_count} ${plan.interval_unit}s`}`
        : 'Recurring membership',
      credits: 'No automatic renewal',
    },
    {
      label: 'Existing credit balance',
      membership: 'Kept for non-included features',
      credits: 'Credits remain in your balance',
    },
    {
      label: 'Cancellation',
      membership: 'Cancel renewal; access continues through the paid period',
      credits: 'Not applicable',
    },
  ];
  const rows = [
    ...rates.map((rate) => ({
      label: rate.name,
      membership: membershipCapability(plan, rate),
      credits: rate.display,
    })),
    ...policyRows,
  ];

  return (
    <section aria-labelledby='compare-title'>
      <div className='mb-4'>
        <h2 id='compare-title' className='font-sans text-20 font-bold text-brand-800'>
          Compare how payment works
        </h2>
        <p className='mt-1 font-sans text-14 text-gray-650'>
          Membership covers only the benefits shown in your purchased plan. Other features continue
          to use credits.
        </p>
      </div>

      <div className='hidden overflow-hidden rounded-16 border border-purple-150 bg-white md:block'>
        <table className='w-full border-collapse text-left font-sans'>
          <caption className='sr-only'>Membership and pay-as-you-go comparison</caption>
          <thead>
            <tr className='bg-purple-25 text-brand-800'>
              <th scope='col' className='w-1/3 px-5 py-4 text-13 font-bold'>
                Benefit
              </th>
              <th scope='col' className='w-1/3 px-5 py-4 text-13 font-bold'>
                Membership
              </th>
              <th scope='col' className='w-1/3 px-5 py-4 text-13 font-bold'>
                Pay as you go
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className='border-t border-purple-150'>
                <th scope='row' className='px-5 py-4 text-13 font-semibold text-brand-800'>
                  {row.label}
                </th>
                <td className='px-5 py-4 text-13 leading-20 text-gray-650'>{row.membership}</td>
                <td className='px-5 py-4 text-13 leading-20 text-gray-650'>{row.credits}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='grid gap-3 md:hidden'>
        {rows.map((row) => (
          <article key={row.label} className='rounded-12 border border-purple-150 bg-white p-4'>
            <h3 className='font-sans text-14 font-bold text-brand-800'>{row.label}</h3>
            <dl className='mt-3 grid gap-3'>
              <div>
                <dt className='font-sans text-11 font-bold uppercase tracking-wide text-gray-530'>
                  Membership
                </dt>
                <dd className='mt-1 font-sans text-13 leading-20 text-gray-650'>
                  {row.membership}
                </dd>
              </div>
              <div>
                <dt className='font-sans text-11 font-bold uppercase tracking-wide text-gray-530'>
                  Pay as you go
                </dt>
                <dd className='mt-1 font-sans text-13 leading-20 text-gray-650'>{row.credits}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
    </section>
  );
}

export function BillingView({ compact = false }: BillingViewProps) {
  const { user } = useAuth();
  const openAlert = useUiStore((state) => state.openAlert);
  const [searchParams] = useSearchParams();
  const checkoutState = searchParams.get('checkout');
  const focusTarget = searchParams.get('focus');
  const pollForSubscription = checkoutState === 'subscription-success';
  const checkoutCanceled = checkoutState?.endsWith('-canceled') ?? false;
  const {
    optionsQuery,
    currentQuery,
    subscriptionCheckout,
    creditCheckout,
    cancelRenewal,
    managementSession,
  } = useBilling(pollForSubscription);
  const [selectedPlanId, setSelectedPlanId] = React.useState<string | null>(null);
  const [pendingDiscountPercent, setPendingDiscountPercent] = React.useState<number | null>(null);
  const membershipOptionsRef = React.useRef<HTMLDivElement>(null);
  const creditOptionsRef = React.useRef<HTMLDivElement>(null);
  const payAsYouGoEnabled = optionsQuery.data?.pay_as_you_go_enabled ?? false;

  React.useEffect(() => {
    if (optionsQuery.isLoading || currentQuery.isLoading) return;
    const target =
      focusTarget === 'membership'
        ? membershipOptionsRef.current
        : focusTarget === 'credits'
          ? creditOptionsRef.current
          : null;
    if (!target) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
  }, [currentQuery.isLoading, focusTarget, optionsQuery.isLoading]);

  React.useEffect(() => {
    if (!payAsYouGoEnabled) {
      setPendingDiscountPercent(null);
      return;
    }
    creditFeedbackService
      .getStatus()
      .then((status) => setPendingDiscountPercent(status.pending_discount?.percent ?? null))
      .catch(() => undefined);
  }, [payAsYouGoEnabled]);

  if (optionsQuery.isLoading || currentQuery.isLoading) {
    return (
      <div className='grid gap-4' aria-busy='true' aria-label='Loading billing options'>
        <div className='h-24 animate-pulse rounded-16 bg-purple-50' />
        <div className='grid gap-4 md:grid-cols-2'>
          <div className='h-80 animate-pulse rounded-16 bg-purple-50' />
          <div className='h-80 animate-pulse rounded-16 bg-purple-50' />
        </div>
      </div>
    );
  }

  if (optionsQuery.isError) {
    return (
      <div className='rounded-16 border border-red-150 bg-white p-6' role='alert'>
        <h2 className='font-sans text-18 font-bold text-brand-800'>Billing options did not load</h2>
        <p className='mt-2 font-sans text-14 text-gray-650'>
          Your account was not changed. Check your connection and try again.
        </p>
        <button
          type='button'
          onClick={() => void optionsQuery.refetch()}
          className='mt-4 inline-flex min-h-11 items-center gap-2 rounded-30 border border-brand-800 px-5 font-sans text-14 font-bold text-brand-800'
        >
          <ArrowPathIcon className='size-4' aria-hidden='true' /> Retry loading
        </button>
      </div>
    );
  }

  const options = optionsQuery.data;
  const currentSubscription = currentQuery.data ?? null;
  const hasMembershipOptions = options.subscription_options.length > 0;
  const availability = resolveBillingAvailability(hasMembershipOptions, payAsYouGoEnabled);
  const availabilityCopy = billingAvailabilityCopy[availability];
  const hasPaymentOptions = availability !== 'none';
  const comparesPaymentModels = availability === 'both';
  const selectedPlan =
    options.subscription_options.find((plan) => plan.plan_version_id === selectedPlanId) ??
    options.subscription_options[0];

  const confirmCancellation = () => {
    if (!currentSubscription) return;
    openAlert({
      title: `Cancel renewal for ${currentSubscription.purchased_plan.name}?`,
      message: `You will not be charged again. Your purchased benefits remain available through ${formatBillingDate(currentSubscription.current_period_end)}; the current paid period is not refunded.`,
      primaryLabel: 'Cancel renewal',
      primaryAction: () => cancelRenewal.mutateAsync(),
      secondaryLabel: 'Keep membership',
    });
  };

  return (
    <div
      className={clsx(
        'flex w-full flex-col gap-10',
        compact ? 'max-w-[1200px]' : 'mx-auto max-w-[1200px]'
      )}
    >
      <header className='max-w-3xl'>
        <h2 className='font-sans text-24 font-bold text-brand-800 md:text-32'>
          {availabilityCopy.heading}
        </h2>
      </header>

      {checkoutState === 'subscription-success' && !currentSubscription && (
        <div className='rounded-12 border border-purple-220 bg-purple-25 p-4' role='status'>
          <p className='font-sans text-14 font-bold text-brand-800'>Payment received</p>
          <p className='mt-1 font-sans text-13 text-gray-650'>
            We are confirming your membership. This page will update automatically.
          </p>
        </div>
      )}

      {checkoutState === 'credit-success' && (
        <div className='rounded-12 border border-purple-220 bg-purple-25 p-4' role='status'>
          <p className='font-sans text-14 font-bold text-brand-800'>Credit payment received</p>
          <p className='mt-1 font-sans text-13 text-gray-650'>
            Your balance will update after payment confirmation.
          </p>
        </div>
      )}

      {checkoutCanceled && (
        <div className='rounded-12 border border-purple-220 bg-purple-25 p-4' role='status'>
          <p className='font-sans text-14 font-bold text-brand-800'>Checkout canceled</p>
          <p className='mt-1 font-sans text-13 text-gray-650'>
            Nothing was charged. Your membership and credit balance are unchanged.
          </p>
        </div>
      )}

      {currentQuery.isError && (
        <div className='rounded-12 border border-yellow-400 bg-yellow-400/10 p-4' role='alert'>
          <p className='font-sans text-14 font-bold text-brand-800'>
            Membership status unavailable
          </p>
          <p className='mt-1 font-sans text-13 text-gray-650'>
            Options are still visible, but do not start another membership checkout until your
            status loads.
          </p>
          <button
            type='button'
            onClick={() => void currentQuery.refetch()}
            className='mt-3 font-sans text-13 font-bold text-brand-800 underline'
          >
            Retry status
          </button>
        </div>
      )}

      {currentSubscription && (
        <CurrentMembershipCard
          subscription={currentSubscription}
          onManage={() => managementSession.mutate()}
          onCancel={confirmCancellation}
          isManaging={managementSession.isPending}
        />
      )}

      <section aria-labelledby='payment-options-title'>
        <div className='mb-5 flex flex-col justify-between gap-2 sm:flex-row sm:items-end'>
          <div>
            <h2 id='payment-options-title' className='font-sans text-20 font-bold text-brand-800'>
              {availabilityCopy.sectionTitle}
            </h2>
            <p className='mt-1 font-sans text-14 text-gray-650'>
              {availabilityCopy.sectionDescription}
            </p>
          </div>
          {payAsYouGoEnabled && (
            <p className='font-sans text-13 text-gray-530'>
              Current balance:{' '}
              <strong className='text-brand-800'>{user?.credits ?? 0} credits</strong>
            </p>
          )}
        </div>

        {hasPaymentOptions ? (
          <div className={clsx('grid gap-6', comparesPaymentModels && 'lg:grid-cols-2')}>
            {hasMembershipOptions && (
              <div
                id='membership-options'
                ref={membershipOptionsRef}
                tabIndex={-1}
                className='flex scroll-mt-6 flex-col gap-4 rounded-20 border border-purple-220 bg-purple-25 p-5 focus:outline-none'
              >
                <div>
                  <p className='font-sans text-12 font-bold uppercase tracking-wide text-purple-680'>
                    Recurring
                  </p>
                  <h3 className='mt-1 font-sans text-20 font-bold text-brand-800'>Membership</h3>
                  <p className='mt-1 font-sans text-13 leading-20 text-gray-650'>
                    Included access renews on the schedule shown for the selected plan.
                  </p>
                </div>
                <div className='grid gap-4'>
                  {options.subscription_options.map((plan) => (
                    <MembershipOption
                      key={plan.plan_version_id}
                      plan={plan}
                      isSelected={plan.plan_version_id === selectedPlan?.plan_version_id}
                      hasCurrentSubscription={Boolean(currentSubscription) || currentQuery.isError}
                      isCheckingOut={
                        subscriptionCheckout.isPending &&
                        subscriptionCheckout.variables === plan.plan_version_id
                      }
                      onSelect={() => setSelectedPlanId(plan.plan_version_id)}
                      onCheckout={() => subscriptionCheckout.mutate(plan.plan_version_id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {payAsYouGoEnabled && (
              <div
                id='credit-options'
                ref={creditOptionsRef}
                tabIndex={-1}
                className='flex scroll-mt-6 flex-col gap-4 rounded-20 border border-gray-250 bg-gray-75 p-5 focus:outline-none'
              >
                <div>
                  <p className='font-sans text-12 font-bold uppercase tracking-wide text-gray-530'>
                    One-time purchase
                  </p>
                  <h3 className='mt-1 font-sans text-20 font-bold text-brand-800'>Pay as you go</h3>
                  <p className='mt-1 font-sans text-13 leading-20 text-gray-650'>
                    Buy credits when you need them. Credits remain in your balance and do not renew.
                  </p>
                </div>
                {pendingDiscountPercent ? (
                  <div className='rounded-12 border border-purple-220 bg-white p-3 font-sans text-13 text-brand-800'>
                    Your next credit purchase has a{' '}
                    <strong>{pendingDiscountPercent}% discount</strong>.
                  </div>
                ) : null}
                {options.credit_packages.length ? (
                  <div className='grid gap-3 sm:grid-cols-2'>
                    {options.credit_packages.map((creditPackage) => (
                      <CreditPackageOption
                        key={creditPackage.id}
                        creditPackage={creditPackage}
                        isPurchasing={
                          creditCheckout.isPending && creditCheckout.variables === creditPackage.id
                        }
                        disabled={creditCheckout.isPending}
                        onPurchase={() => creditCheckout.mutate(creditPackage.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className='rounded-12 border border-gray-250 bg-white p-5'>
                    <p className='font-sans text-14 font-semibold text-brand-800'>
                      Credit packages are temporarily unavailable
                    </p>
                    <p className='mt-1 font-sans text-13 text-gray-650'>
                      Your existing balance is unchanged. Try again later.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className='rounded-16 border border-gray-250 bg-gray-75 p-5'>
            <p className='font-sans text-14 font-semibold text-brand-800'>
              No payment options are available right now
            </p>
            <p className='mt-1 font-sans text-13 leading-20 text-gray-650'>
              Your current membership and existing credits are unchanged. Please check back later.
            </p>
          </div>
        )}
      </section>

      {comparesPaymentModels && (
        <ComparisonTable plan={selectedPlan} rates={options.credit_usage_rates} />
      )}
      <div className='h-4' />
    </div>
  );
}
